import type { ReportChartConfiguration } from '@vision/ui/interfaces/report-chart-configuration';
import type { TableDispatchConfiguration } from '@vision/ui/interfaces/table-dispatch-configuration';
import { t } from 'i18next';
import { array, object, string } from 'yup';
import { TableDispatchValue } from '../../components/ReportsDetailGraphicChartsRenderer/ReportsDetailGraphicChartsValueRenderer/TableDispatchValue';
import { DefaultReportChartThemeFont, GRAPHIC_TITLE_MAX_CHAR_COUNT } from '../default-chart.config';

export const DEFAULT_TABLE_DISPATCH_CONFIGURATION: TableDispatchConfiguration = {
  channelId: null,
  channelType: null,
  flowId: null,
  schemaSelections: [],
};

export const TableDispatchDefaultConfiguration: ReportChartConfiguration = {
  validationSchema: object().shape({
    selectedDate: object().shape({
      fromDate: string().required('validation:rules.generic.required'),
      toDate: string().required('validation:rules.generic.required'),
      dateRange: string().optional(),
    }),
    selectedMeasure: string().oneOf(['participation-rate']).required('validation:rules.generic.required'),
    selectedCompare: array()
      .of(string())
      .test('dispatch-schema-compare', 'validation:rules.generic.required', (value) => {
        return value?.length === 1 && value[0] === 'dispatch-schema';
      }),
    selectedTableDispatch: object().shape({
      channelType: string().required('validation:rules.generic.required'),
      channelId: string().required('validation:rules.generic.required'),
      flowId: string().required('validation:rules.generic.required'),
      schemaSelections: array()
        .of(
          object().shape({
            fieldCode: string().required('validation:rules.generic.required'),
            fieldId: string().required('validation:rules.generic.required'),
            fieldName: string().required('validation:rules.generic.required'),
            values: array().of(string().required()).defined(),
          }),
        )
        .min(1, 'validation:rules.generic.required')
        .max(10, 'validation:rules.generic.max')
        .test('unique-dispatch-schema-fields', 'validation:rules.generic.unique', (value) => {
          const fieldCodes = value?.map(({ fieldCode }) => fieldCode) ?? [];
          return new Set(fieldCodes).size === fieldCodes.length;
        }),
    }),
    selectedTheme: object().shape({
      title: object().shape({
        label: string()
          .max(
            GRAPHIC_TITLE_MAX_CHAR_COUNT,
            t('validation:rules.generic.max', {
              count: GRAPHIC_TITLE_MAX_CHAR_COUNT,
            }),
          )
          .required('validation:rules.reports.selectedTheme.title.label.required'),
      }),
    }),
  }),
  measures: [{ type: 'channel-metric', items: ['participation-rate'] }],
  measureReadOnly: true,
  keepConfigurationTabsMounted: true,
  hideCompare: true,
  compareRendererComponent: null,
  customize: {
    hide: true,
  },
  defaultTheme: {
    active: () => true,
    title: {
      ...DefaultReportChartThemeFont,
      label: '',
    },
    dataLabels: {
      ...DefaultReportChartThemeFont,
      active: false,
      position: 'center',
    },
  },
  filters: () => [],
  hideFilters: true,
  defaultMeasure: 'participation-rate',
  defaultCompares: ['dispatch-schema'],
  defaultTableDispatch: DEFAULT_TABLE_DISPATCH_CONFIGURATION,
  chartValueComponent: TableDispatchValue,
};
